import { Skeleton, useToast } from "@design";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type ComponentProps, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage, fetchApiFile, isApiFilePath } from "@/api";
import { saveBlob } from "@/lib/download";
import { Text } from "../Text";

type ElementProps<T extends "a" | "img"> = ComponentProps<T> & { node?: unknown };

// Streamdown's built-in link look, which a `components` override replaces, but in
// the brand *text* token: `text-primary` (brand-primary #ff5500) is a fill color
// and only reaches 3.06:1 on light surfaces, failing WCAG AA for body text.
/** Inline-link style shared by every markdown surface (and links rendered next to markdown). */
export const MARKDOWN_LINK_CLASS = "wrap-anywhere font-medium text-brand-primary-on-subtle underline";
const LINK_CLASS = MARKDOWN_LINK_CLASS;
const IMAGE_CLASS = "max-w-full rounded-lg";

function textOf(children: unknown): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(textOf).join("");
  return "";
}

function fallbackName(href: string, label: string): string {
  const fromLabel = label.replace(/^download\s+/i, "").trim();
  return /\.\w{2,5}$/.test(fromLabel) ? fromLabel : (href.split("/").filter(Boolean).at(-2) ?? "download");
}

/** Markdown link: backend paths download with the user's token; other links open in a new tab. */
export function MarkdownLink({ href, children, node: _node, ...rest }: Readonly<ElementProps<"a">>) {
  const { t } = useTranslation("chat", { keyPrefix: "markdown" });
  const toast = useToast();
  const download = useMutation({
    mutationFn: (path: string) => fetchApiFile(path),
    onSuccess: (file, path) => saveBlob(file.blob, file.filename ?? fallbackName(path, textOf(children))),
    onError: (error) => toast.error(errorMessage(error, t("downloadFailed"))),
  });

  if (!href || !isApiFilePath(href)) {
    return (
      <a {...rest} className={LINK_CLASS} data-streamdown="link" href={href} rel="noreferrer" target="_blank">
        {children}
      </a>
    );
  }
  return (
    <a
      {...rest}
      className={LINK_CLASS}
      data-streamdown="link"
      href={href}
      aria-busy={download.isPending || undefined}
      onClick={(event) => {
        event.preventDefault();
        if (!download.isPending) download.mutate(href);
      }}
    >
      {children}
    </a>
  );
}

function ApiImage({ src, alt }: Readonly<{ src: string; alt?: string }>) {
  const { t } = useTranslation("chat", { keyPrefix: "markdown" });
  const file = useQuery({
    queryKey: ["chat", "api-file", src],
    queryFn: () => fetchApiFile(src),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const url = useMemo(() => (file.data ? URL.createObjectURL(file.data.blob) : null), [file.data]);
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);

  if (file.isPending) return <Skeleton className="h-48 w-full max-w-md" />;
  if (!url) {
    return (
      <Text variant="caption" tone="tertiary">
        {t("imageNotAvailable")}
      </Text>
    );
  }
  return <img className={IMAGE_CLASS} data-streamdown="image" src={url} alt={alt ?? ""} />;
}

/** Markdown image: backend paths are fetched with the user's token. */
export function MarkdownImage({ src, alt, node: _node, ...rest }: Readonly<ElementProps<"img">>) {
  if (typeof src === "string" && isApiFilePath(src)) return <ApiImage src={src} alt={alt} />;
  return <img {...rest} className={IMAGE_CLASS} data-streamdown="image" src={src} alt={alt ?? ""} />;
}
